import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { PlanTaskCreate, PlanTaskPatch } from '../types'

// Per-event Project Plan tasks (spec 32). Cache-keyed by ['plan-tasks',
// eventId]; create / patch / delete all invalidate the same key so the
// Gantt refetches after every mutation. Mirrors the useActions.ts hook
// shape exactly for consistency across CRUD tabs.

export function useEventPlanTasks(eventId: number) {
  return useQuery({
    queryKey: ['plan-tasks', eventId],
    queryFn: () => ApiClient.eventPlanTasks(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useCreatePlanTask(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PlanTaskCreate) => ApiClient.createPlanTask(eventId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan-tasks', eventId] })
  })
}

export function usePatchPlanTask(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: PlanTaskPatch }) =>
      ApiClient.patchPlanTask(eventId, id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan-tasks', eventId] })
  })
}

export function useDeletePlanTask(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deletePlanTask(eventId, id).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plan-tasks', eventId] })
  })
}
